/**
 * InlineEditor - Inline text editing for preview runtime
 *
 * Enables inline editing of text fields in preview mode by making elements
 * contenteditable and sending updates to the admin panel via sendToParent.
 *
 * Messages sent (flat, matching admin panel expectations):
 *   { type: 'field-edited', sectionId, field, value }
 *   { type: 'element-selected', sectionId, field, elementType: 'text' | 'image' | 'list-item', content, itemIndex? }
 */

import type { MessageHandler } from './message-handler.js';

export class InlineEditor {
  private messageHandler: MessageHandler;
  private editableElements: Set<HTMLElement> = new Set();
  private imageElements: Set<HTMLImageElement> = new Set();
  private highlightedListItem: HTMLElement | null = null;
  private isViewOnly = false;
  private editabilityMap: Record<string, Record<string, boolean>> = {};

  constructor(messageHandler: MessageHandler) {
    this.messageHandler = messageHandler;
  }

  /**
   * Set view-only mode (disables all inline editing)
   */
  setViewOnly(viewOnly: boolean): void {
    this.isViewOnly = viewOnly;
  }

  /**
   * Set editability map (nested: sectionId -> field -> boolean)
   */
  setEditabilityMap(map: Record<string, Record<string, boolean>>): void {
    this.editabilityMap = map;
  }

  /**
   * Initialize inline editing on all data-dr-field text elements.
   * Called on init and after each full-update to catch new DOM nodes.
   */
  setupInlineEditing(): void {
    if (this.isViewOnly) return;

    const fields = document.querySelectorAll('[data-dr-field]');

    fields.forEach((el) => {
      const htmlEl = el as HTMLElement;
      const tagName = htmlEl.tagName.toLowerCase();

      // Skip non-text elements
      if (tagName === 'img' || tagName === 'video' || tagName === 'iframe' || tagName === 'input') return;

      // Check editability from map
      const sectionId = this.getSectionId(htmlEl);
      const field = htmlEl.getAttribute('data-dr-field');
      if (sectionId && field && this.editabilityMap[sectionId]?.[field] === false) return;

      // Skip if already set up
      if (htmlEl.getAttribute('data-dr-editable') === 'true') return;
      htmlEl.setAttribute('data-dr-editable', 'true');

      // Determine if this field is inside an array item
      const listItemEl = htmlEl.closest('[data-dr-list-item]');
      const listContext = this.getListContext(htmlEl, listItemEl as HTMLElement | null);

      htmlEl.setAttribute('contenteditable', 'true');
      htmlEl.style.outline = 'none';
      htmlEl.style.cursor = 'text';

      this.editableElements.add(htmlEl);

      htmlEl.addEventListener('focus', () => {
        htmlEl.style.outline = '2px solid rgba(139, 92, 246, 0.5)';
        htmlEl.style.outlineOffset = '2px';
        htmlEl.style.borderRadius = '2px';

        const sid = this.getSectionId(htmlEl);
        const f = htmlEl.getAttribute('data-dr-field');
        if (sid && f) {
          const msg: Record<string, any> = {
            type: 'element-selected',
            sectionId: sid,
            field: listContext ? listContext.listName : f,
            elementType: listContext ? 'list-item' : 'text',
            content: htmlEl.textContent || '',
          };
          if (listContext) {
            msg.itemIndex = listContext.itemIndex;
          }
          this.messageHandler.sendToParent(msg);
        }
      });

      htmlEl.addEventListener('blur', () => {
        htmlEl.style.outline = 'none';
        htmlEl.style.outlineOffset = '';
        htmlEl.style.borderRadius = '';

        const sid = this.getSectionId(htmlEl);
        const f = htmlEl.getAttribute('data-dr-field');
        if (sid && f) {
          const msg: Record<string, any> = {
            type: 'field-edited',
            sectionId: sid,
            field: f,
            value: htmlEl.textContent || '',
          };
          if (listContext) {
            msg.listName = listContext.listName;
            msg.itemIndex = listContext.itemIndex;
          }
          this.messageHandler.sendToParent(msg);
        }
      });

      htmlEl.addEventListener('input', () => {
        const sid = this.getSectionId(htmlEl);
        const f = htmlEl.getAttribute('data-dr-field');
        if (sid && f) {
          const msg: Record<string, any> = {
            type: 'field-edited',
            sectionId: sid,
            field: f,
            value: htmlEl.textContent || '',
          };
          if (listContext) {
            msg.listName = listContext.listName;
            msg.itemIndex = listContext.itemIndex;
          }
          this.messageHandler.sendToParent(msg);
        }
      });
    });

    // Ensure editable fields remain clickable despite data-dr-effect pointer-events:none
    this.restoreEditability();

    // Set up click-to-select on list items (no inline editing, just selection)
    this.setupListItemClickHandlers();
  }

  /**
   * Set up click handlers on all img[data-dr-field] elements.
   * Sends element-selected + image-replace-requested on click.
   */
  setupImageClickHandlers(): void {
    if (this.isViewOnly) return;

    const images = document.querySelectorAll('img[data-dr-field]');

    images.forEach((img) => {
      const htmlImg = img as HTMLImageElement;

      // Check editability
      const sectionId = this.getSectionId(htmlImg);
      const field = htmlImg.getAttribute('data-dr-field');
      if (sectionId && field && this.editabilityMap[sectionId]?.[field] === false) return;

      // Skip if already set up
      if (htmlImg.getAttribute('data-dr-img-handler') === 'true') return;
      htmlImg.setAttribute('data-dr-img-handler', 'true');

      htmlImg.style.cursor = 'pointer';
      htmlImg.style.transition = 'outline 0.15s ease';

      this.imageElements.add(htmlImg);

      htmlImg.addEventListener('mouseenter', () => {
        htmlImg.style.outline = '2px solid rgba(139, 92, 246, 0.5)';
        htmlImg.style.outlineOffset = '2px';
      });

      htmlImg.addEventListener('mouseleave', () => {
        htmlImg.style.outline = '';
        htmlImg.style.outlineOffset = '';
      });

      htmlImg.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const sid = this.getSectionId(htmlImg);
        const f = htmlImg.getAttribute('data-dr-field');

        if (sid && f) {
          this.messageHandler.sendToParent({
            type: 'element-selected',
            sectionId: sid,
            field: f,
            elementType: 'image',
            content: htmlImg.src,
          });

          this.messageHandler.sendToParent({
            type: 'image-replace-requested',
            sectionId: sid,
            field: f,
          });
        }
      });
    });
  }

  /**
   * Set up click handlers on data-dr-list-item elements so clicking a list item
   * in the preview iframe sends element-selected with the item index.
   * The admin panel uses this to scroll to and expand the corresponding repeater item.
   */
  setupListItemClickHandlers(): void {
    if (this.isViewOnly) return;

    const listItems = document.querySelectorAll('[data-dr-list-item]');

    listItems.forEach((item) => {
      const htmlItem = item as HTMLElement;

      // Skip if already set up
      if (htmlItem.getAttribute('data-dr-list-handler') === 'true') return;
      htmlItem.setAttribute('data-dr-list-handler', 'true');

      htmlItem.style.cursor = 'pointer';

      htmlItem.addEventListener('click', (e) => {
        // Don't interfere with clicks on images or links inside the item
        const target = e.target as HTMLElement;
        if (target.tagName.toLowerCase() === 'a' || target.tagName.toLowerCase() === 'button') return;

        const sectionId = this.getSectionId(htmlItem);
        const listEl = htmlItem.closest('[data-dr-list]');
        const listName = listEl?.getAttribute('data-dr-list') || '';

        if (!sectionId || !listName) return;

        // Calculate item index among siblings
        const siblings = listEl!.querySelectorAll('[data-dr-list-item]');
        let itemIndex = -1;
        siblings.forEach((sibling, i) => {
          if (sibling === htmlItem) itemIndex = i;
        });

        if (itemIndex < 0) return;

        // Clear previous highlight
        if (this.highlightedListItem) {
          this.highlightedListItem.style.outline = '';
          this.highlightedListItem.style.outlineOffset = '';
        }

        // Highlight clicked item
        htmlItem.style.outline = '2px solid rgba(139, 92, 246, 0.5)';
        htmlItem.style.outlineOffset = '-2px';
        this.highlightedListItem = htmlItem;

        // Send selection to admin panel
        this.messageHandler.sendToParent({
          type: 'element-selected',
          sectionId,
          field: listName,
          elementType: 'list-item',
          itemIndex,
          content: '',
        });
      });
    });
  }

  /**
   * Re-force pointer-events: auto on editable fields that have data-dr-effect
   * containing pointer-events:none. Must be called after every condition evaluation
   * since the condition evaluator resets inline styles.
   */
  restoreEditability(): void {
    this.editableElements.forEach((el) => {
      const effectAttr = el.getAttribute('data-dr-effect') || '';
      if (effectAttr.includes('pointer-events')) {
        el.style.pointerEvents = 'auto';
      }
    });
  }

  /**
   * Resolve the section ID from the nearest data-dr-section ancestor
   */
  private getSectionId(el: HTMLElement): string | null {
    const section = el.closest('[data-dr-section]');
    return section?.getAttribute('data-dr-section') || null;
  }

  /**
   * If the element is inside a data-dr-list-item, return the list name and item index.
   * Returns null if not inside a list item.
   */
  private getListContext(el: HTMLElement, listItemEl: HTMLElement | null): { listName: string; itemIndex: number } | null {
    if (!listItemEl) return null;

    const listEl = listItemEl.closest('[data-dr-list]');
    if (!listEl) return null;

    const listName = listEl.getAttribute('data-dr-list') || '';
    if (!listName) return null;

    const siblings = listEl.querySelectorAll('[data-dr-list-item]');
    let itemIndex = -1;
    siblings.forEach((sibling, i) => {
      if (sibling === listItemEl) itemIndex = i;
    });

    return itemIndex >= 0 ? { listName, itemIndex } : null;
  }

  destroy(): void {
    this.editableElements.clear();
    this.imageElements.clear();
    if (this.highlightedListItem) {
      this.highlightedListItem.style.outline = '';
      this.highlightedListItem = null;
    }
  }
}
